import React, { Component } from 'react'
import PropTypes from 'prop-types'
import ToggleMultiLaunch from './ToggleMultiLaunch'
import styles from './user.scss'
import Tooltip from '../Tooltip'
import TemporaryLaunchFrame from '../../containers/TemporaryLaunchFrame'
import { launcherQuitProcess } from '../../launcherBackend/launcher-command'

const Span = (props) => {
    const settings = {}
    if (props.onClick != null) settings.onClick = props.onClick
    if (props.className != null) settings.className = props.className

    return (
        <Tooltip content={props.title} position="right">
            <span aria-hidden {...settings}>
                <i className={`flaticon flaticon-${props.icon}`} />
            </span>
        </Tooltip>
    )
}

Span.propTypes = {
    title: PropTypes.string,
    icon: PropTypes.string,
    onClick: PropTypes.func,
    className: PropTypes.string,
}

class User extends Component {
    constructor(props) {
        super(props)

        // There is also the possibility of pushing this into redux as well
        // fx under user.showLaunchFrame and then this component becomes stateless
        this.state = { showLaunchFrame: false }
    }

    componentDidMount() {
        try {
            // Auto-launch on login
            if (this.props.user !== undefined && this.props.user.autoLaunch) {
                if (!this.props.playEnabled) {
                    console.info(
                        'Auto-play on login request could not be completed, launcher is updating files.',
                    )
                    return
                }

                // Start a client for the user
                this.startClient()

                // Change autolaunch for the user to false
                this.props.setAutoLaunch()
            }
        } catch (error) {
            console.info(
                `Encountered an error while trying to auto-launch client: ${error.message}`,
            )
        }
    }

    componentDidUpdate(prevProps) {
        // the launchClient has changed, and current one matches
        // the username for this object we start a client
        if (
            prevProps.launchClient !== this.props.launchClient &&
            this.props.launchClient === this.props.user.username
        ) {
            this.startClient()
        }
    }

    onChange = () => {
        // Update multi-launch state for the user
        this.props.setMultiLaunchState()
    }

    startClient = () => {
        if (!this.props.playEnabled) {
            console.info(
                `Start client request for ${this.props.user.username} on ${
                    this.props.server.shortName
                } could not be completed. Launcher is updating files.`,
            )
            return
        }

        console.info(
            `Start client request for ${this.props.user.username} on ${
                this.props.server.shortName
            }`,
        )
        this.setState({ showLaunchFrame: true })
        if (this.props.process.progress === 0) {
            this.props.setClientLaunchProgress(1)
        }
    }

    stopClient = () => {
        console.info(
            `Stop client request for ${this.props.user.username} on ${this.props.server.shortName}`,
        )
        launcherQuitProcess(this.props.server.shortName, this.props.user.username)
        this.props.setClientLaunchProgress(0)
    }

    removeTemporaryLaunchFrame = () => {
        this.setState({ showLaunchFrame: false })
    }

    // Remove user
    removeUser = () => {
        this.props.remove()
    }

    selectProfile = () => {
        this.props.toggleProfileSelector(
            this.ref.getBoundingClientRect(),
            this.props.localize('profile').replace('{username}', this.props.user.username),
        )
    }

    // Toggle user pinned state
    togglePin = () => {
        this.props.togglePinnedState()
    }

    renderLaunchFrame = () => {
        if (!this.state.showLaunchFrame) {
            return null
        }

        try {
            return (
                <TemporaryLaunchFrame
                    userKey={this.props.user.username}
                    unMount={() => this.removeTemporaryLaunchFrame()}
                />
            )
        } catch (error) {
            console.info(
                `Encountered an error while creating a temporary launch frame: ${error.message}`,
            )
            return null
        }
    }

    renderToggle = () => {
        // If we aren't supposed to show the toggle, return null
        if (!this.props.displayToggle) return null

        // Render the toggle for multi-launch
        return (
            <ToggleMultiLaunch
                id={`multi-launch-${this.props.user.hash}`}
                name={`multi-launch-${this.props.user.hash}`}
                update={() => this.onChange()}
                value={this.props.user.multiLaunch}
                styles={styles}
                playing={this.props.process.isRunning}
            />
        )
    }

    renderStartOrStop = () => {
        // If the server is not ready...
        if (this.props.playEnabled !== undefined && !this.props.playEnabled) return null

        if (this.props.process.isRunning || this.props.process.progress > 0) {
            return (
                <Span
                    icon="stop46"
                    onClick={() => this.stopClient()}
                    title={this.props
                        .localize('stopClient')
                        .replace('{username}', this.props.user.username)}
                />
            )
        }

        return (
            <Span
                icon="play106"
                onClick={() => this.startClient()}
                title={this.props
                    .localize('startClient')
                    .replace('{username}', this.props.user.username)}
            />
        )
    }

    renderProfileButton = () => (
        <li>
            <Span
                title={this.props
                    .localize('profile')
                    .replace('{username}', this.props.user.username)}
                icon="interface"
                onClick={() => this.selectProfile()}
            />
        </li>
    )

    renderRemoveButton = () => (
        <li>
            <Span
                title={this.props.localize('remove')}
                icon="delete85"
                onClick={() => this.removeUser()}
            />
        </li>
    )

    renderPinButton = () => {
        // Styles
        const pinClass = this.props.user.remember ? styles.pinned : ''

        // Text localization
        const pinText = this.props.localize(this.props.user.remember ? 'unpin' : 'pin')

        return (
            <li>
                <Span
                    title={pinText}
                    icon="pin2"
                    onClick={() => this.togglePin()}
                    className={pinClass}
                />
            </li>
        )
    }

    renderLaunching = () => {
        if (this.props.process.progress === 0) {
            return null
        }

        const className =
            this.props.process.progress === 100 && this.props.process.isRunning
                ? `${styles.launching} ${styles.starting}`
                : styles.launching

        return (
            <div className={className}>
                <p>{this.props.localize(this.props.process.progressName)}</p>
                <progress max="100" value={this.props.process.progress} />
            </div>
        )
    }

    render() {
        if (this.props.user === undefined) return null

        try {
            // Styles
            let listClasses = styles.account
            if (this.props.process.isRunning && this.props.process.progress === 0) {
                listClasses = `${styles.account} ${styles.playing}`
            }
            let userNameClass = styles.username
            if (this.props.process.isRunning) userNameClass = `${styles.username} ${styles.playing}`

            // Rendering for the multi-launch toggle
            const toggle = this.renderToggle()

            // Rendering for the launch-frame
            const launchFrame = this.renderLaunchFrame()

            // Rendering for the start/stop buttons
            const actionButton = this.renderStartOrStop()

            // Render the profile, remove and pin buttons
            const profileButton = this.renderProfileButton()
            const removeButton = this.renderRemoveButton()
            const pinButton = this.renderPinButton()
            const launching = this.renderLaunching()

            // const profileSelector = this.renderProfileSelector()

            return (
                <li className={listClasses} data-id={this.props.user.hash}>
                    <div className={styles.account_details}>
                        {launchFrame}
                        <div className={styles.buttons}>
                            {toggle}
                            {actionButton}
                        </div>
                        <div className={userNameClass}>{this.props.user.username}</div>
                        <div className={styles.buttons} ref={ref => (this.ref = ref)}>
                            <ul>
                                {profileButton}
                                {removeButton}
                                {pinButton}
                            </ul>
                        </div>
                    </div>
                    {launching}
                </li>
            )
        } catch (error) {
            console.info(`Encountered an error while rendering user: ${error.message}`)
            return null
        }
    }
}

User.propTypes = {
    user: PropTypes.shape({
        hash: PropTypes.string,
        profile: PropTypes.string,
        remember: PropTypes.bool,
        multiLaunch: PropTypes.bool,
        autoLaunch: PropTypes.bool,
        tokens: PropTypes.shape({
            accessToken: PropTypes.string,
            refreshToken: PropTypes.string,
        }),
        username: PropTypes.string,
    }),
    server: PropTypes.shape({
        enabled: PropTypes.bool,
        name: PropTypes.string,
        numPlayers: PropTypes.number,
        playButton: PropTypes.bool,
        shortName: PropTypes.string,
        showPlayers: PropTypes.bool,
        sso: PropTypes.shape({
            slinger: PropTypes.string,
            url: PropTypes.string,
        }),
        status: PropTypes.string,
        statusLabel: PropTypes.string,
        websiteUrl: PropTypes.string,
    }),
    playEnabled: PropTypes.bool,
    togglePinnedState: PropTypes.func,
    launchClient: PropTypes.string,
    setAutoLaunch: PropTypes.func,
    remove: PropTypes.func,
    localize: PropTypes.func,
    setMultiLaunchState: PropTypes.func,
    process: PropTypes.shape({
        isRunning: PropTypes.bool,
        progress: PropTypes.number,
        progressName: PropTypes.string,
    }),
    displayToggle: PropTypes.bool,
    setClientLaunchProgress: PropTypes.func,
    toggleProfileSelector: PropTypes.func,
}

export default User



// WEBPACK FOOTER //
// ./src/components/User/user.js